import * as React from 'react';

import {
  countries,
  getEmojiFlag,
  type TCountryCode,
  type ICountry,
} from 'countries-list';

import { Select, type SelectProps } from '../pickers/Select';

interface CountryItem {
  id: TCountryCode;
  data: ICountry;
}

export interface CountryComboBoxProps extends Omit<
  SelectProps<string>,
  'options' | 'onSelectionChange'
> {
  /**
   * Optional filter for countries to display
   * If provided, only countries with codes in this array will be shown
   */
  countryCodes?: TCountryCode[];

  /**
   * Whether to show country flags next to names
   * @default true
   */
  showFlags?: boolean;

  /**
   * Custom render function for displaying each country
   */
  renderCountry?: (country: CountryItem) => React.ReactNode;

  /**
   * Whether to display the native name of the country alongside or instead of the English name
   * @default false
   */
  showNativeName?: boolean;

  /**
   * Whether to include the country code in the display
   * @default false
   */
  showCountryCode?: boolean;

  onSelectionChange?: (value: string | undefined) => void;
}

const defaultRenderCountry = (
  showFlags: boolean,
  showNativeName: boolean,
  showCountryCode: boolean,
  country: CountryItem,
) => (
  <div className="flex items-center gap-0.5">
    {showFlags && <span className="w-5">{getEmojiFlag(country.id)}</span>}
    {!showNativeName && <span>{country.data.name}</span>}
    {showNativeName && <span>{country.data.native}</span>}
    {showCountryCode && <span>[{country.id}]</span>}
  </div>
);

/**
 * A ComboBox component specialized for selecting countries
 * Uses the countries-list package for data
 *
 * @example
 * ```tsx
 * <CountryComboBox
 *   label="Select your country"
 *   placeholder="Search countries..."
 *   onSelectionChange={(country) => console.log(country)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With filtered countries
 * <CountryComboBox
 *   label="Select your country"
 *   countryCodes={['US', 'CA', 'MX']}
 *   showFlags={true}
 *   showCountryCode={true}
 * />
 * ```
 */
export const SelectCountry = ({
  countryCodes,
  showFlags = true,
  showNativeName = false,
  showCountryCode = false,
  renderCountry,
  placeholder = 'Search country...',
  onSelectionChange,
  ...props
}: CountryComboBoxProps) => {
  const countryArray = React.useMemo(
    () =>
      Object.entries(countries).map(([code, data]) => ({
        code: code as TCountryCode,
        data,
      })),
    [],
  );

  const options = React.useMemo(() => {
    let filteredCountries = countryArray;

    if (countryCodes && countryCodes.length > 0) {
      filteredCountries = filteredCountries.filter((country) =>
        countryCodes.includes(country.code),
      );
    }

    return filteredCountries
      .sort((a, b) => a.data.name.localeCompare(b.data.name))
      .map((c) => ({
        key: c.code,
        label: renderCountry
          ? renderCountry({ id: c.code, data: c.data })
          : defaultRenderCountry(showFlags, showNativeName, showCountryCode, {
              id: c.code,
              data: c.data,
            }),
      }));
  }, [
    countryArray,
    countryCodes,
    renderCountry,
    showFlags,
    showNativeName,
    showCountryCode,
  ]);

  const handleSelectionChange = (value: string | undefined) => {
    onSelectionChange?.(value);
  };

  return (
    <Select<string>
      options={options}
      placeholder={placeholder}
      onChange={handleSelectionChange}
      {...props}
      className="@SelectCountry"
    />
  );
};

// CountryComboBoxItems is no longer needed as the new Select component
// handles rendering internally through the options prop
